import { SCAN_PROCESS_STEPS, PATHS } from '../constants/flowConfig';
import { loadBkDesignOrderContext } from './bkDesignOrderContext';
import { pickPrimaryMeshSourceEntry } from './bkScanSourceFiles';

/**
 * True when `useBkScanAutoLoad` will load `source_files` on this route
 * (checkpoint / finalization loaders handle mesh elsewhere).
 */
export function getPendingSourceMeshAutoLoad(pathname, scanFilesMeta) {
  const sourceFiles = scanFilesMeta?.source_files;
  if (!Array.isArray(sourceFiles) || sourceFiles.length === 0) return null;

  // Raw scan needs the heavy phased load (merge vertices, topology repair). Only
  // run it on the first step. Other routes restore from their checkpoints; a
  // direct load there should not re-process the raw scan.
  if (pathname !== PATHS[SCAN_PROCESS_STEPS.PREPARING_SCAN]) {
    return null;
  }

  return pickPrimaryMeshSourceEntry(sourceFiles);
}

/**
 * Decide what ThreeScene should load: patient upload, ERP mesh (via auto-load),
 * or nothing — and whether to keep the loading overlay up while waiting.
 *
 * @returns {{ modelSource: object|string|null, awaiting: boolean }}
 */
export function sceneHasLoadedMesh(sceneRefs) {
  return !!(sceneRefs?.shadedMesh?.geometry?.attributes?.position?.count);
}

export function resolveSceneModelSource({
  uploadedModelData,
  isLoadingPatientData,
  isFetchingScanFiles,
  scanFilesMeta,
  scanAutoLoaded,
  patientData,
  patientDataError,
  pathname,
  bkCheckpointHydrating,
}) {
  if (uploadedModelData) {
    return { modelSource: uploadedModelData, awaiting: false };
  }

  const hasOrderContext = !!loadBkDesignOrderContext();

  if (isLoadingPatientData || isFetchingScanFiles) {
    return { modelSource: null, awaiting: true };
  }

  // A server checkpoint mesh is being fetched/downloaded (network latency can
  // outlast the scan-files fetch). Keep the scene "awaiting" so the overlay is
  // not torn down before the mesh is set — otherwise the model pops into an
  // empty scene after the loader has already disappeared.
  if (bkCheckpointHydrating) {
    return { modelSource: null, awaiting: true };
  }

  // Order context exists but hydration has not started yet (navigation race).
  if (hasOrderContext && scanFilesMeta == null && patientData == null && !patientDataError) {
    return { modelSource: null, awaiting: true };
  }

  const pendingAutoLoad = getPendingSourceMeshAutoLoad(pathname, scanFilesMeta);
  if (pendingAutoLoad && !scanAutoLoaded) {
    return { modelSource: null, awaiting: true };
  }

  return { modelSource: null, awaiting: false };
}
